
import { memo, useEffect, useState, useMemo } from "react";
import { motion } from "framer-motion";
import { RecipeInput } from "@/components/RecipeInput";
import { LoadingSpinner } from "@/components/LoadingSpinner";
import { RecipeTransformation } from "@/components/RecipeTransformation";
import { PricingModal } from "@/components/PricingModal";
import { CheckoutHandler } from "@/components/subscription/CheckoutHandler";
import { useUpgradeHandler } from "@/components/subscription/UpgradeHandler";
import { SubscriptionStatus } from "@/services/SubscriptionService";
import { useRecipeTransformation } from "@/hooks/useRecipeTransformation";
import { RecipeTransformationHeader } from "@/components/recipe/RecipeTransformationHeader";
import { RecipeTransformationGuide } from "@/components/recipe/RecipeTransformationGuide";
import { SubscriptionStatusIndicator } from "@/components/recipe/SubscriptionStatusIndicator";

interface RecipeTransformationSectionProps {
  user: any;
  subscriptionStatus: SubscriptionStatus | null;
  onLoadSubscriptionStatus: (userId: string) => Promise<SubscriptionStatus | null>;
  forceRefresh?: () => void;
  isLoading?: boolean;
}

// Create the component first, then wrap it with memo
const RecipeTransformationSectionComponent = ({ 
  user, 
  subscriptionStatus, 
  onLoadSubscriptionStatus,
  forceRefresh,
  isLoading: subscriptionLoading
}: RecipeTransformationSectionProps) => {
  const [subscriptionChecked, setSubscriptionChecked] = useState(false);
  const [hasInitialized, setHasInitialized] = useState(false);
  
  // Use the extracted recipe transformation hook with memoization
  const {
    isLoading,
    recipeData,
    showPricingModal,
    setShowPricingModal,
    lastTransformTime,
    handleRecipeSubmit
  } = useRecipeTransformation({
    user,
    subscriptionStatus,
    onLoadSubscriptionStatus,
    forceRefresh
  });
  
  // Use the extracted upgrade handler with memoization to prevent re-renders
  const { handleUpgradeClick, checkoutUrl, isLoading: upgradeLoading, setIsLoading: setUpgradeLoading } = 
    useUpgradeHandler(user);

  // Determine if we're in a loading state
  const isComponentLoading = isLoading || subscriptionLoading;
  
  // Handle manual refresh of subscription status
  const handleManualRefresh = () => {
    if (forceRefresh && user) {
      forceRefresh();
      // Also set subscription_activated flag to force a thorough check
      localStorage.setItem('subscription_activated', 'true');
      localStorage.setItem('subscription_activated_time', Date.now().toString());
    }
  };
  
  // After each transformation completes, refresh the subscription status
  // Use useEffect with a proper dependency array to prevent unnecessary re-renders
  useEffect(() => {
    if (lastTransformTime > 0 && user && forceRefresh) {
      const timeoutId = setTimeout(() => {
        console.log('Auto-refreshing subscription status after transformation');
        forceRefresh();
      }, 1000);
      
      return () => clearTimeout(timeoutId);
    }
  }, [lastTransformTime, user, forceRefresh]);

  // Initialize once to prevent flashing
  useEffect(() => {
    if (!hasInitialized && user) {
      setHasInitialized(true);
    }
  }, [user, hasInitialized]);

  // Memoize the content to prevent re-renders
  const content = useMemo(() => (
    <motion.div 
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="bg-white/95 backdrop-blur-sm rounded-2xl p-6 sm:p-8 space-y-6 sm:space-y-10 shadow-xl border border-primary/10 transition-all duration-300"
    >
      {/* Hidden component for checkout handling logic */}
      <CheckoutHandler
        user={user}
        subscriptionStatus={subscriptionStatus}
        forceRefresh={forceRefresh}
        subscriptionChecked={subscriptionChecked}
        setSubscriptionChecked={setSubscriptionChecked}
      />
      
      {user && subscriptionStatus && (
        <SubscriptionStatusIndicator 
          subscriptionStatus={subscriptionStatus}
          onRefresh={handleManualRefresh}
        />
      )}
      
      <div className="max-w-2xl mx-auto space-y-4 sm:space-y-6">
        <RecipeTransformationHeader />
        
        {/* How-to Guide Collapsible */}
        <RecipeTransformationGuide />
        
        <RecipeInput onSubmit={handleRecipeSubmit} />
      </div>
      
      {isComponentLoading && <LoadingSpinner />}
      {recipeData && <RecipeTransformation recipeData={recipeData} />}

      <PricingModal 
        isOpen={showPricingModal}
        onClose={() => setShowPricingModal(false)}
        onUpgradeClick={handleUpgradeClick}
        checkoutUrl={checkoutUrl}
        isLoading={upgradeLoading}
      />
    </motion.div>
  ), [
    user, 
    subscriptionStatus, 
    forceRefresh, 
    subscriptionChecked, 
    handleManualRefresh, 
    handleRecipeSubmit, 
    isComponentLoading, 
    recipeData, 
    showPricingModal, 
    setShowPricingModal, 
    handleUpgradeClick, 
    checkoutUrl, 
    upgradeLoading
  ]);

  // Only render once initialized to prevent flashing
  if (!hasInitialized && user) {
    return <div className="min-h-[500px] flex items-center justify-center">
      <LoadingSpinner />
    </div>;
  }

  return content;
};

// Create a proper memoized version of the component with a more efficient comparison
export const RecipeTransformationSection = memo(
  RecipeTransformationSectionComponent,
  (prevProps, nextProps) => {
    // Custom comparison to prevent unnecessary rerenders
    return (
      prevProps.user?.id === nextProps.user?.id &&
      JSON.stringify(prevProps.subscriptionStatus) === JSON.stringify(nextProps.subscriptionStatus) &&
      prevProps.isLoading === nextProps.isLoading
    );
  }
);
